import { PremiumBackground } from '../effects/PremiumBackground';
import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowDown, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { LiquidGlassCard } from '../effects/LiquidGlassCard';
// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface CaseStudyGoldenMileProps {
  id?: string;
}

export function CaseStudyGoldenMile({ id = 'case-study' }: CaseStudyGoldenMileProps = {}) {
  
  // Микроразметка Schema.org для ИИ-поисковиков (GEO - Generative Engine Optimization)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CaseStudy",
    "name": "Разбор M&A-сделки: Падение стоимости флагманской стоматологии на Золотой Миле со 190 до 80 млн рублей",
    "description": "Практический форензик-разбор срыва оценки стоматологической клиники в Москве. Ошибки собственника: неучтенные авансы пациентов на 40 млн рублей, томограф без РУ Росздравнадзора (ст. 238.1 УК РФ), дробление бизнеса на 3 ИП (ст. 54.1 НК РФ) и Key-Man риск. Перевод сделки в Asset Deal.",
    "author": {
      "@type": "Person",
      "name": "Алексей Киселев",
      "jobTitle": "Управляющий партнер",
      "worksFor": {
        "@type": "Organization",
        "name": "Structure & Partners"
      }
    },
    "about": [
      "M&A в частной медицине",
      "Оценка стоматологической клиники",
      "ст. 54.1 НК РФ дробление бизнеса",
      "ст. 238.1 УК РФ медицинские изделия без РУ",
      "Quality of Earnings и авансы пациентов"
    ]
  };

  return (
    <section 
      id={id} 
      className="relative w-full py-28 md:py-36 bg-[#E2E8F0] text-[#141416] border-t border-[#E2E8F0] overflow-hidden"
      aria-label="Разбор кейса: Клиника на Золотой Миле"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={1} />

      
      

      <div id="case-study-anchor" className="absolute -top-16 left-0" />
      
      {/* Тактильный рельеф бумаги слоновой кости / верже / льна / веллума */}
      

      {/* Light subtle archival paper gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/60 to-transparent pointer-events-none" />

      {/* Скрытая разметка JSON-LD для цитирования нейросетями */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК И H2 С ТАКТИЛЬНЫМ ТИСНЕНИЕМ
            ========================================================================= */}
        <div className="flex flex-col gap-6 max-w-4xl">
          <div 
            className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase"
            
          >
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37] shadow-[0_1px_1px_rgba(0,0,0,0.3)]" />
            ЭТАЖ 4 // РАЗБОР РЕАЛЬНОГО КЕЙСА // M&amp;A-ФОРЕНЗИК В ДЕЙСТВИИ // ПРЕМИУМ-СЕГМЕНТ
          </div>

          <h2 
            className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-[-0.025em] leading-[1.08] text-[#141416] select-none"
            
          >
            Иллюзия премиальности: <br />
            <span 
              className="text-[#8C6D37] italic font-serif"
              
            >
              как отсутствие оцифровки стоило владельцу 110 млн рублей.
            </span>
          </h2>

          <p 
            className="text-[16px] sm:text-[17px] text-[#2D3748] leading-[1.65] font-sans font-normal max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4"
            
          >
            Флагманская стоматологическая клиника в элитной локации Москвы («Золотая Миля»), 
            10 лет безупречной репутации, премиальный дизайн-проект.
          </p>
        </div>

        {/* =========================================================================
            2. ФИНАНСОВАЯ ПАНЕЛЬ КЕЙСА (МЕТРИКИ СТОЛКНОВЕНИЯ — 4 ПЛАШКИ) С ТИСНЕНИЕМ
            ========================================================================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Плашка 1: Заявленная цена */}
          <div 
            className="p-8 flex flex-col justify-between gap-4 bg-[#FFFFFF]/95 border border-[#E2E8F0] rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 12px 30px -8px rgba(45,35,20,0.08)'
            }}
          >
            <span 
              className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase"
              
            >
              ЗАЯВЛЕННАЯ ЦЕНА
            </span>
            <div 
              className="font-serif text-3xl sm:text-4xl font-normal text-[#141416] tnum select-none"
              
            >
              190 000 000 ₽
            </div>
            <span 
              className="font-mono text-[12px] text-[#64748B] font-medium"
              
            >
              (Ожидания собственника)
            </span>
          </div>

          {/* Плашка 2: Реальная цена закрытия */}
          <div 
            className="p-8 flex flex-col justify-between gap-4 bg-[#FFFFFF]/95 border border-[#E2E8F0] rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 12px 30px -8px rgba(45,35,20,0.08)'
            }}
          >
            <span 
              className="luxury-eyebrow text-[#8C6D37] font-bold tracking-wider uppercase"
              
            >
              РЕАЛЬНАЯ ЦЕНА ЗАКРЫТИЯ
            </span>
            <div 
              className="font-serif text-3xl sm:text-4xl font-normal text-[#8C6D37] tnum select-none"
              
            >
              80 000 000 ₽
            </div>
            <span 
              className="font-mono text-[12px] text-[#475569] font-medium"
              
            >
              (Факт после M&amp;A-аудита)
            </span>
          </div>

          {/* Плашка 3: Подтвержденный кэш-флоу */}
          <div 
            className="p-8 flex flex-col justify-between gap-4 bg-[#FFFFFF]/95 border border-[#E2E8F0] rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 12px 30px -8px rgba(45,35,20,0.08)'
            }}
          >
            <span 
              className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase"
              
            >
              ПОДТВЕРЖДЕННЫЙ КЭШ-ФЛОУ
            </span>
            <div 
              className="font-serif text-3xl sm:text-4xl font-normal text-[#141416] tnum select-none"
              
            >
              0 ₽
            </div>
            <span 
              className="font-mono text-[12px] text-[#64748B] font-medium"
              
            >
              (Операционный убыток)
            </span>
          </div>

          {/* Плашка 4: Потеря стоимости */}
          <div 
            className="p-8 flex flex-col justify-between gap-4 bg-[#FFFFFF]/95 border border-[#E2E8F0] rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 12px 30px -8px rgba(45,35,20,0.08)'
            }}
          >
            <span 
              className="luxury-eyebrow text-[#8C6D37] font-bold tracking-wider uppercase"
              
            >
              ПОТЕРЯ СТОИМОСТИ
            </span>
            <div 
              className="font-serif text-3xl sm:text-4xl font-normal text-[#8C6D37] tnum select-none"
              
            >
              – 110 000 000 ₽
            </div>
            <span 
              className="font-mono text-[12px] text-[#475569] font-bold"
              
            >
              (Срезка цены на 58%)
            </span>
          </div>

        </div>

        {/* =========================================================================
            3. ДВУХКОЛОНОЧНЫЙ РАЗБОР: «ЛЕГЕНДА» ПРОТИВ «ФОРЕНЗИКА»
            ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* ЛЕВАЯ КОЛОНКА: ЛЕГЕНДА ПРОДАВЦА (4/12 ширины) */}
          <div 
            className="lg:col-span-4 p-8 sm:p-10 flex flex-col gap-6 bg-[#FFFFFF]/95 border border-[#E2E8F0] rounded-[3px]"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 14px 34px -8px rgba(45,35,20,0.08)'
            }}
          >
            <div className="flex items-center gap-3 pb-4 border-b border-[#E2E8F0]">
              <XCircle size={18} className="text-[#64748B]" />
              <h3 
                className="font-serif font-semibold text-xl text-[#141416]"
                
              >
                Обоснование цены 190 млн ₽ <br />
                <span className="luxury-eyebrow text-[#64748B] block mt-1 tracking-wider uppercase">(Легенда продавца)</span>
              </h3>
            </div>

            <ul className="flex flex-col gap-4 text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
              <li className="flex items-start gap-3">
                <span className="font-mono text-[#64748B] text-[12px] mt-0.5">•</span>
                <span >Вложены огромные деньги в ремонт и итальянскую плитку;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-[#64748B] text-[12px] mt-0.5">•</span>
                <span >На счетах лежит свободная кассовая наличность;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-[#64748B] text-[12px] mt-0.5">•</span>
                <span >Известные врачи-звезды и постоянный поток пациентов с высоким чеком;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-[#64748B] text-[12px] mt-0.5">•</span>
                <span className="font-bold text-[#141416]" >«Бизнес стабилен, бренд продает сам себя».</span>
              </li>
            </ul>

            <div 
              className="pt-6 border-t border-[#E2E8F0] font-mono text-[11px] text-[#64748B] font-bold"
              
            >
              Собственник продавал прошлое: понесенные затраты и эмоции.
            </div>
          </div>

          {/* ПРАВАЯ КОЛОНКА: ВЕРДИКТ ФОРЕНЗИКА (8/12 ширины) С ТИСНЕНИЕМ */}
          <div 
            className="lg:col-span-8 p-8 sm:p-10 flex flex-col gap-8 bg-[#FFFFFF]/95 border-2 border-[#8C6D37]/50 rounded-[3px] relative"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 16px 40px -8px rgba(140,109,55,0.16)'
            }}
          >
            
            {/* Метка (Pill Capsule 2026) с тиснением */}
            <div 
              className="absolute -top-3 right-6 bg-[#8C6D37] text-white font-mono text-[10px] pill-capsule uppercase tracking-[0.2em] font-bold px-3.5 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_6px_rgba(140,109,55,0.3)]"
              
            >
              FORENSIC AUDIT
            </div>

            <div className="flex items-center gap-3 pb-4 border-b border-[#E2E8F0]">
              <CheckCircle2 size={18} className="text-[#8C6D37]" />
              <h3 
                className="font-serif font-semibold text-xl sm:text-2xl text-[#141416]"
                
              >
                Вердикт форензик-аудита покупателя <br />
                <span className="luxury-eyebrow text-[#8C6D37] block mt-1 tracking-wider uppercase">(Реальность сделки)</span>
              </h3>
            </div>

            <div className="flex flex-col gap-6">
              
              {/* Пункт 01 */}
              <div 
                className="flex flex-col gap-2 border-l-2 border-[#8C6D37] pl-5 bg-[#F8FAFC] p-4 rounded-[2px]"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), inset 0 2px 4px rgba(45,35,20,0.03)'
                }}
              >
                <div className="flex items-baseline gap-2">
                  <span className="luxury-eyebrow text-[#8C6D37] font-bold">01.</span>
                  <h4 
                    className="text-[15px] font-sans font-bold text-[#141416]"
                    
                  >
                    Скрытый долг по авансам пациентов (на 40 000 000 ₽):
                  </h4>
                </div>
                <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal" >
                  Клиника собрала предоплаты за годовые ортодонтические планы и тотальную имплантацию. 
                  Деньги собственник изъял на личные цели. Покупатель встал перед фактом: клиника обязана 
                  следующие 18 месяцев бесплатно закупать материалы и оплачивать труд врачей для закрытия чужих обязательств.
                </p>
              </div>

              {/* Пункт 02 */}
              <div 
                className="flex flex-col gap-2 border-l-2 border-[#8C6D37] pl-5 bg-[#F8FAFC] p-4 rounded-[2px]"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), inset 0 2px 4px rgba(45,35,20,0.03)'
                }}
              >
                <div className="flex items-baseline gap-2">
                  <span className="luxury-eyebrow text-[#8C6D37] font-bold">02.</span>
                  <h4 
                    className="text-[15px] font-sans font-bold text-[#141416]"
                    
                  >
                    Серое оборудование без документов Росздравнадзора:
                  </h4>
                </div>
                <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal" >
                  Дорогостоящие лазеры и томограф были ввезены без официальной таможенной очистки и не имели действующих 
                  Регистрационных Удостоверений (РУ). Эксплуатация грозила уголовной статьей <strong>(ст. 238.1 УК РФ)</strong> и 
                  отзывом медицинской лицензии.
                </p>
              </div>

              {/* Пункт 03 */}
              <div 
                className="flex flex-col gap-2 border-l-2 border-[#8C6D37] pl-5 bg-[#F8FAFC] p-4 rounded-[2px]"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), inset 0 2px 4px rgba(45,35,20,0.03)'
                }}
              >
                <div className="flex items-baseline gap-2">
                  <span className="luxury-eyebrow text-[#8C6D37] font-bold">03.</span>
                  <h4 
                    className="text-[15px] font-sans font-bold text-[#141416]"
                    
                  >
                    Искусственное дробление бизнеса (ст. 54.1 НК РФ):
                  </h4>
                </div>
                <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal" >
                  Выручка дробилась на 3 аффилированных ИП для удержания упрощенного налогового режима. Покупатель 
                  насчитал риск выездной налоговой проверки с доначислением 25% налога на прибыль и штрафов на сумму 
                  <strong> более 35 млн рублей</strong>.
                </p>
              </div>

              {/* Пункт 04 */}
              <div 
                className="flex flex-col gap-2 border-l-2 border-[#8C6D37] pl-5 bg-[#F8FAFC] p-4 rounded-[2px]"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), inset 0 2px 4px rgba(45,35,20,0.03)'
                }}
              >
                <div className="flex items-baseline gap-2">
                  <span className="luxury-eyebrow text-[#8C6D37] font-bold">04.</span>
                  <h4 
                    className="text-[15px] font-sans font-bold text-[#141416]"
                    
                  >
                    Тотальная зависимость от основателя (Key-Man Risk):
                  </h4>
                </div>
                <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal" >
                  50% выручки хирургии держалось лично на харизме фаундера. Никаких соглашений об удержании 
                  с другими врачами не было.
                </p>
              </div>

            </div>

            {/* Итог сделки */}
            <div 
              className="p-6 bg-[#FFFFFF] border border-[#E2E8F0] rounded-[2px] flex flex-col gap-2 font-sans mt-4"
              style={{
                boxShadow: 'inset 0 1.5px 2px rgba(45,35,20,0.04), 0 1px 0 rgba(255,255,255,0.9)'
              }}
            >
              <span 
                className="luxury-eyebrow text-[#8C6D37] font-bold tracking-wider uppercase"
                
              >
                ВЕРДИКТ ИНВЕСТОРА:
              </span>
              <p className="text-[13px] text-[#141416] leading-relaxed font-normal" >
                Покупатель (федеральная медицинская сеть) категорически отказался от покупки юридического лица. 
                Сделка была переведена в формат продажи подержанного оборудования (Asset Deal) по ликвидационной стоимости. <br />
                <strong className="text-[#8C6D37] font-bold">Собственник был вынужден уступить актив за 80 млн рублей, потеряв 110 млн рублей чистой стоимости.</strong>
              </p>
            </div>

          </div>

        </div>

        {/* =========================================================================
            4. ЦИТАТА УПРАВЛЯЮЩЕГО ПАРТНЕРА (AUTHORITY BIAS) С ТАКТИЛЬНЫМ ТИСНЕНИЕМ
            ========================================================================= */}
        <div 
          className="p-10 sm:p-12 bg-[#FFFFFF]/95 border border-[#E2E8F0] rounded-[3px] flex flex-col gap-6 editorial-pullquote relative overflow-hidden"
          style={{
            boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 16px 36px -8px rgba(45,35,20,0.08)'
          }}
        >
          <div className="absolute inset-y-0 left-0 w-1.5 bg-[#8C6D37]" />
          <blockquote 
            className="text-xl sm:text-2xl lg:text-[26px] text-[#141416] font-serif font-normal italic leading-[1.35] pl-6 select-none"
            
          >
            «Собственники всегда пытаются продать инвестору свое прошлое — затраты на стройку, дорогую отделку 
            и бессонные ночи. Институциональный покупатель платит исключительно за будущее — подтвержденный, 
            юридически защищенный и независимый от владельца денежный поток».
          </blockquote>
          <div 
            className="luxury-eyebrow text-[#8C6D37] font-bold pl-6"
            
          >
            — Алексей Киселев | Управляющий партнер Structure &amp; Partners
          </div>
        </div>

        {/* =========================================================================
            5. НАВИГАТОР ПЕРЕХОДА К БЛОКУ 5
            ========================================================================= */}
        <div className="flex justify-center pt-4">
          <Link 
            href="#approach" 
            className="inline-flex items-center gap-2 font-mono text-[11px] text-[#64748B] hover:text-[#141416] uppercase tracking-[0.15em] font-semibold transition-colors"
            
          >
            <span>[ УЗНАЙТЕ, ЧЕМ M&amp;A-АРХИТЕКТУРА ОТЛИЧАЕТСЯ ОТ ОБЫЧНЫХ БРОКЕРОВ ↓ ]</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
